import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Form } from "assets/styleds";
import React, { FC, useEffect, useState } from "react";
import { useAppSelector } from "store";
import * as styles from "assets/styles/form";
import { Controller, useForm } from "react-hook-form";
import {
  IFiltroCurso,
  IFiltroRelatorioCoordenacao,
  IFiltroSerie,
  IFiltroTurma,
  IMatricula,
  IPeriodoEtapa,
  listaRelatoriosCoordenacao,
  relatorioConferenciaConteudoMinistrado,
  schemaFiltroRelatorioCoordenacao,
  utils,
} from "@deltasge/marauders-map";
import { yupResolver } from "@hookform/resolvers/yup";
import Axios from "axios";
import { api, Environment } from "configs";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { parseISO } from "date-fns";
import {
  Printer as PrintIcon,
  FileCog as ConfigIcon,
  RadioboxBlank as RadioBlankIcon,
  RadioboxMarked as RadioMarkedIcon,
} from "mdi-material-ui";
import { DatePicker } from "@mui/x-date-pickers";
import { getDefault, PageConfig } from "./PageConfig";
import { useDialog } from "hooks/useDialog";
import qs from "qs";

export const RelatorioCoordenacao: FC<{
  close: () => void;
  anoLetivo?: number;
  idCurso?: number;
  idSerie?: number;
  idTurma?: number;
  idMateria?: number;
  etapa?: number;
  dataIni?: Date;
  dataFim?: Date;
}> = ({
  close,
  anoLetivo: anoLetivoInicial,
  idCurso: idCursoInicial,
  idSerie: idSerieInicial,
  idTurma: idTurmaInicial,
  idMateria: idMateriaInicial,
  etapa: etapaInicial,
  dataIni: dataIniInicial,
  dataFim: dataFimInicial,
}) => {
  const CancelToken = Axios.CancelToken;

  const {
    configPapelPadrao,
    treeView: { professores, cursos },
    usuario: { usuario },
    escola,
    sistemaAvaliacao: { data: sistemaAvaliacao },
    configuracoes: {
      etapas: todasEtapas,
      configOnline: { configuracoes },
    },
    layout: { devMode },
  } = useAppSelector((state) => state);

  const [matricula, setMatricula] = useState<{
    data: IMatricula[];
    loading: boolean;
  }>({
    data: [],
    loading: false,
  });
  const [papel, setPapel] = useState(getDefault(configPapelPadrao));
  const [imprimirEm, setImprimirEm] = useState<"pdf" | "html" | "json">("pdf");
  const [submiting, setSubmiting] = useState(false);

  const {
    RenderDialog: PageConfigDialog,
    hide: hidePageConfig,
    show: openPageConfig,
  } = useDialog({
    title: "Configurar Página",
    options: {
      modal: true,
      scroll: "paper",
      maxWidth: "sm",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IFiltroRelatorioCoordenacao>({
    defaultValues: {
      ...schemaFiltroRelatorioCoordenacao.getDefault(),
      dataEmissao: new Date(),
      idRelatorio: relatorioConferenciaConteudoMinistrado.id,
      anoLetivo: anoLetivoInicial?.toString() ?? escola.anoSite,
      idCurso: idCursoInicial,
      idSerie: idSerieInicial,
      idTurma: idTurmaInicial,
      idMateria: idMateriaInicial,
      etapa: etapaInicial ?? configuracoes.bim_site,
      dataIni: dataIniInicial,
      dataFim: dataFimInicial,
    },
    resolver: yupResolver(schemaFiltroRelatorioCoordenacao),
  });

  const idRelatorio = watch("idRelatorio");
  const idProfessor = watch("idProfessor");
  const idCurso = watch("idCurso");
  const idSerie = watch("idSerie");
  const idTurma = watch("idTurma");
  const idMatricula = watch("idMatricula");
  const idMateria = watch("idMateria");
  const anoLetivo = watch("anoLetivo");
  const etapa = watch("etapa");

  const nomeEtapa = sistemaAvaliacao?.nomeEtapa || "Bimestre";

  const relatorio = listaRelatoriosCoordenacao.find((f) => f.id == idRelatorio);

  let curso: IFiltroCurso | undefined = undefined;
  if (idCurso) {
    curso = utils.getCursoTreeView({
      cursos,
      type: usuario?.tipoUsuario,
      idCurso: idCurso.toString() ?? null,
    });
  }

  let serie: IFiltroSerie | undefined = undefined;
  if (idSerie) {
    serie = utils.getSerieTreeView({
      type: usuario?.tipoUsuario,
      cursos,
      idCurso: idCurso?.toString() ?? null,
      idSerie: idSerie.toString() ?? null,
    });
  }

  let turma: IFiltroTurma | undefined = undefined;
  if (idTurma) {
    turma = utils.getTurmaTreeView({
      type: usuario?.tipoUsuario,
      cursos,
      idCurso: idCurso?.toString() ?? null,
      idSerie: idSerie?.toString() ?? null,
      idTurma: idTurma.toString() ?? null,
    });
  }

  const materias = utils.getMateriasTreeView({
    serie,
    turma,
    sort: true,
  });

  let etapas: IPeriodoEtapa[] | undefined = undefined;
  if (curso) {
    etapas = todasEtapas.filter((etapa) => {
      return (
        etapa.idsCursos?.includes(curso?.id ?? 0) &&
        etapa.etapa <= configuracoes.bim_site
      );
    });
  }

  const onSubmit = async (payload: IFiltroRelatorioCoordenacao) => {
    try {
      setSubmiting(true);

      const configPagina = {
        width: `${papel.width}mm`,
        height: `${papel.height}mm`,
        marginTop: `${papel.marginTop}mm`,
        marginBottom: `${papel.marginBottom}mm`,
        marginLeft: `${papel.marginLeft}mm`,
        marginRight: `${papel.marginRight}mm`,
      };

      const url = Environment.RELATORIO_API_URL.concat("/api/")
        .concat(escola.codigo)
        .concat("/coordenacao/conferencia-conteudo/?")
        .concat(
          qs.stringify({
            ...payload,
            ...configPagina,
            authorization: usuario?.token,
            imprimirEm: devMode ? imprimirEm : "pdf",
          })
        );
      window.open(url);
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSubmiting(false);
    }
  };

  useEffect(() => {
    let ativo = true;
    const source = CancelToken.source();
    (async () => {
      try {
        const habilita = relatorio?.habilita?.includes("idMatricula") || false;
        if (!idTurma || !habilita) return;

        if (ativo) {
          setMatricula((p) => ({ ...p, loading: true }));
        }
        const { data } = await api.get<IMatricula[]>(`matricula`, {
          cancelToken: source.token,
          params: {
            select: [
              "id",
              "idAluno",
              "nrChamada",
              "aluno",
              "aluno.nome",
              "aluno.nomeusual",
            ],
            where: {
              idTurma,
              "situacao.in": utils.enumMatriculasAtivas,
            },
            order: { nrChamada: "ASC" },
          },
        });
        if (ativo) setMatricula((p) => ({ ...p, data }));
      } catch (error) {
        if (Axios.isCancel(error)) return;
        if (ativo) snack.error(getError(error));
      } finally {
        if (ativo) {
          setMatricula((p) => ({ ...p, loading: false }));
        }
      }
    })();
    return () => {
      ativo = false;
      source.cancel();
    };
  }, [idTurma, relatorio?.habilita]);

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
        <PageConfigDialog>
          <PageConfig
            item={papel}
            handleClose={(data) => {
              if (data) setPapel(data);
              hidePageConfig();
            }}
          />
        </PageConfigDialog>
        <DialogContent dividers style={{ paddingBottom: 0 }}>
          <Form
            noValidate
            id="relatorio-coordenacao-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Seleção</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idProfessor
                            .spec.label
                        }
                        select
                        error={!!errors.idProfessor}
                        helperText={errors.idProfessor?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        value={idProfessor || ""}
                        disabled={
                          relatorio?.habilita?.includes("idProfessor") != true
                        }
                        onChange={({ target: { value } }) => {
                          if (value)
                            setValue("idProfessor", parseInt(value, 10));
                          else setValue("idProfessor", null);
                          setValue("idCurso", null);
                          setValue("idSerie", null);
                          setValue("idTurma", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                          setValue("idMateria", null);
                        }}
                      >
                        <MenuItem value="">
                          <b>Todos os professores</b>
                        </MenuItem>
                        {professores.map((item) => (
                          <MenuItem key={`prof-${item.id}`} value={item.id}>
                            {item.nome}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.anoLetivo.spec
                            .label
                        }
                        onChange={({ target: { value } }) => {
                          setValue("anoLetivo", value);
                          setValue("idCurso", null);
                          setValue("idSerie", null);
                          setValue("idTurma", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                          setValue("idMateria", null);
                          setValue("idProfessor", null);
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={anoLetivo || ""}
                        type="number"
                        disabled={
                          relatorio?.habilita?.includes("anoLetivo") != true
                        }
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        select
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idCurso.spec
                            .label
                        }
                        value={idCurso || ""}
                        onChange={({ target: { value } }) => {
                          if (value) setValue("idCurso", parseInt(value, 10));
                          else setValue("idCurso", null);
                          setValue("idSerie", null);
                          setValue("idTurma", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                          setValue("idMateria", null);
                        }}
                        error={!!errors.idCurso}
                        helperText={errors.idCurso?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          relatorio?.habilita?.includes("idCurso") != true
                        }
                      >
                        <MenuItem value="">
                          <b>Todos os cursos</b>
                        </MenuItem>
                        {cursos.map((c, index) => (
                          <MenuItem key={`curso-${index}`} value={`${c.id}`}>
                            {c.nome?.rUpper()}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idSerie.spec
                            .label
                        }
                        value={idSerie || ""}
                        onChange={({ target: { value } }) => {
                          if (value) setValue("idSerie", parseInt(value, 10));
                          else setValue("idSerie", null);
                          setValue("idTurma", null);
                          setValue("idMatricula", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                          setValue("idMateria", null);
                        }}
                        error={!!errors.idSerie}
                        helperText={errors.idSerie?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          relatorio?.habilita?.includes("idSerie") != true
                        }
                      >
                        <MenuItem value="">
                          <b>Todas as séries</b>
                        </MenuItem>
                        {curso?.series?.map((serie, index) => (
                          <MenuItem
                            key={`serie-${index}`}
                            value={`${serie.id}`}
                          >
                            {serie.descricao.rUpper()}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idTurma.spec
                            .label
                        }
                        value={idTurma || ""}
                        onChange={({ target: { value } }) => {
                          if (value) setValue("idTurma", parseInt(value, 10));
                          else setValue("idTurma", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                        }}
                        error={!!errors.idTurma}
                        helperText={errors.idTurma?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          relatorio?.habilita?.includes("idTurma") != true
                        }
                      >
                        <MenuItem value="">
                          <b>Todas as turmas</b>
                        </MenuItem>
                        {serie?.turmas?.map((turma, index) => (
                          <MenuItem
                            key={`turma-${index}`}
                            value={`${turma.id}`}
                          >
                            {turma.tituloTurma}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idMateria.spec
                            .label
                        }
                        value={idMateria || ""}
                        onChange={({ target: { value } }) => {
                          if (value) setValue("idMateria", parseInt(value, 10));
                          else setValue("idMateria", null);
                          setValue("idMatricula", null);
                          setValue("idAluno", null);
                        }}
                        error={!!errors.idMateria}
                        helperText={errors.idMateria?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          relatorio?.habilita?.includes("idMateria") != true
                        }
                      >
                        <MenuItem value="">
                          <b>Todas as matérias</b>
                        </MenuItem>
                        {materias.map((mat, index) => (
                          <MenuItem
                            key={`materia-${index}`}
                            value={`${mat.id}`}
                          >
                            {mat.nome}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label={
                          schemaFiltroRelatorioCoordenacao.fields.idAluno.spec
                            .label
                        }
                        value={idMatricula || ""}
                        onChange={({ target: { value } }) => {
                          if (value) {
                            const idMatricula = parseInt(value, 10);
                            setValue("idMatricula", idMatricula);
                            setValue(
                              "idAluno",
                              matricula.data.find((f) => f.id == idMatricula)
                                ?.aluno?.uid ?? null
                            );
                          } else {
                            setValue("idMatricula", null);
                            setValue("idAluno", null);
                          }
                        }}
                        error={!!errors.idMatricula}
                        helperText={errors.idMatricula?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          endAdornment: !matricula.loading ? undefined : (
                            <InputAdornment position="end" sx={{ mr: 3 }}>
                              <CircularProgress
                                sx={({ spacing }) => ({
                                  height: spacing(3).concat("!important"),
                                  width: spacing(3).concat("!important"),
                                })}
                              />
                            </InputAdornment>
                          ),
                        }}
                        disabled={
                          relatorio?.habilita?.includes("idMatricula") != true
                        }
                      >
                        <MenuItem value="">
                          <b>Todos os alunos</b>
                        </MenuItem>
                        {matricula.data.map((mat, index) => (
                          <MenuItem
                            key={`matricula-${index}`}
                            value={`${mat.id}`}
                          >
                            {`${mat.nrChamada} - ${(
                              mat.aluno?.nomeusual || mat.aluno?.nome
                            )?.rUpper()}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Etapa</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label={nomeEtapa}
                        value={idCurso ? etapa : ""}
                        onChange={({ target: { value } }) => {
                          if (!value) return;
                          const etapa = etapas?.find(
                            (f) => f.etapa == parseInt(value, 10)
                          );
                          if (!etapa) return;
                          setValue("etapa", etapa.etapa);
                          setValue("dataIni", parseISO(etapa.dataEtapaIni));
                          setValue("dataFim", parseISO(etapa.dataEtapaFim));
                        }}
                        error={!!errors.etapa}
                        helperText={errors.etapa?.message}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          relatorio?.habilita?.includes("etapa") != true
                        }
                      >
                        {!idCurso && (
                          <MenuItem value="">Selecione o Curso</MenuItem>
                        )}
                        {etapas
                          ?.sort((a, b) => a.etapa - b.etapa)
                          .map((option, index) => (
                            <MenuItem key={index} value={option.etapa}>
                              {option.etapa}º {nomeEtapa}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Controller
                        control={control}
                        name="dataIni"
                        render={({ field, fieldState }) => (
                          <DatePicker
                            {...field}
                            label={
                              schemaFiltroRelatorioCoordenacao.fields.dataIni
                                .spec.label
                            }
                            value={field.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Controller
                        control={control}
                        name="dataFim"
                        render={({ field, fieldState }) => (
                          <DatePicker
                            {...field}
                            label={
                              schemaFiltroRelatorioCoordenacao.fields.dataFim
                                .spec.label
                            }
                            value={field.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Relatórios</Typography>
                  <List
                    dense
                    disablePadding
                    sx={({ spacing }) => ({
                      maxHeight: spacing(44),
                      overflowY: "auto",
                    })}
                  >
                    {listaRelatoriosCoordenacao
                      .sort((a, b) => a.nome.localeCompare(b.nome))
                      .map((relatorio, index, arr) => {
                        const ultimoRel = index == arr.length - 1;
                        const selecionado = relatorio.id == idRelatorio;
                        return (
                          <ListItem
                            key={`modelo-${relatorio.id}`}
                            disablePadding
                            divider={!ultimoRel}
                          >
                            <ListItemButton
                              onClick={() =>
                                setValue("idRelatorio", relatorio.id)
                              }
                            >
                              <ListItemIcon>
                                {selecionado ? (
                                  <RadioMarkedIcon color="primary" />
                                ) : (
                                  <RadioBlankIcon />
                                )}
                              </ListItemIcon>
                              <ListItemText primary={relatorio.nome} />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Form>
        </DialogContent>
      </Box>
      <DialogActions>
        {devMode && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={imprimirEm}
            sx={{ mr: 2 }}
            onChange={(_, value) => {
              if (value) setImprimirEm(value);
            }}
          >
            <ToggleButton value="pdf">PDF</ToggleButton>
            <ToggleButton value="html">HTML</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          color="inherit"
          startIcon={<ConfigIcon />}
          onClick={openPageConfig}
        >
          Configurar página
        </Button>
        <Button onClick={close} color={"secondary"}>
          Fechar
        </Button>
        <LoadingButton
          type="submit"
          variant="outlined"
          form="relatorio-coordenacao-form"
          loading={submiting}
          startIcon={<PrintIcon />}
        >
          Visualizar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
